import { useStore } from '@nanostores/react';
import { memo, useCallback, useState } from 'react';
import { ContainerWithBackButton } from '~/components/dialogs/settings/ContainerWithBackButton';
import { IconButton } from '~/components/ui/IconButton';
import { Table, TableHead, TableRow } from '~/components/ui/Table';
import { BOLT_HOSTING_DNS, COPIED_MESSAGE_TIMEOUT } from '~/lib/constants';
import { importStore } from '~/lib/projects';
import { domainsStore } from '~/lib/stores/domains';

export const ResolveDnsIssue = memo(() => {
  const entry = useStore(domainsStore.dnsIssueEntry);
  const { deploymentInfo } = useStore(importStore);

  if (!entry) {
    return null;
  }

  const onCancel = useCallback(() => {
    domainsStore.resetStep();
  }, []);

  if (entry.isApex) {
    // apex domains show different instructions

    return (
      <ContainerWithBackButton className="gap-6" title="DNS configuration" onClose={onCancel}>
        <p className="text-bolt-ds-textSecondary">{entry.domain}</p>

        <section className="flex flex-col gap-4 text-bolt-ds-textSecondary text-sm">
          {entry.isPrimary && (
            <p className="text-sm text-bolt-ds-textSecondary">
              <span className="font-semibold text-bolt-ds-textPrimary">Warning</span>: With your current configuration
              of this apex domain being your primary custom domain, you won't benefit from the full advantages of a CDN.
              We recommend setting <span className="font-semibold text-bolt-ds-textPrimary">www.{entry.domain}</span> as
              your primary custom domain.
            </p>
          )}

          <h3 className="text-base text-bolt-ds-textPrimary">Recommended</h3>

          <p>
            If your DNS provider supports ALIAS, ANAME, or flattened CNAME records, use this recommended configuration,
            which is more resilient than the fallback option.
          </p>

          <p>
            Create an ALIAS, ANAME, or flattened CNAME record for <span className="font-semibold">{entry.domain}</span>{' '}
            pointing to our load balancer at <span className="font-semibold">{BOLT_HOSTING_DNS}</span>.
          </p>

          <Table className="w-full">
            <TableHead className="grid grid-cols-[1fr_3fr_4fr] gap-2">
              <span>Type</span>
              <span>Name</span>
              <span>Value</span>
            </TableHead>

            <TableRow className="grid grid-cols-[1fr_3fr_4fr] gap-2 bg-bolt-ds-surface text-bolt-ds-textPrimary">
              <p className="min-w-0">ALIAS</p>
              <CellWithCopyButton text={entry.domain} tooltip="Copy name" />
              <CellWithCopyButton text={BOLT_HOSTING_DNS} tooltip="Copy value" />
            </TableRow>
          </Table>
        </section>

        <section className="flex flex-col gap-4 text-bolt-ds-textSecondary text-sm">
          <h3 className="text-base text-bolt-ds-textPrimary">Fallback</h3>

          <p>
            If your DNS provider does not support ALIAS, ANAME, or flattened CNAME records, use this fallback option.
          </p>

          <p>
            Create an A record for <span className="font-semibold">{entry.domain}</span> pointing to our load balancer's
            IP address <span className="font-semibold">75.2.60.5</span>.
          </p>

          <Table className="w-full">
            <TableHead className="grid grid-cols-[1fr_3fr_4fr] gap-2">
              <span>Type</span>
              <span>Name</span>
              <span>Value</span>
            </TableHead>

            <TableRow className="grid grid-cols-[1fr_3fr_4fr] gap-2 bg-bolt-ds-surface text-bolt-ds-textPrimary">
              <p className="min-w-0">A</p>
              <CellWithCopyButton text={entry.domain} tooltip="Copy name" />
              <CellWithCopyButton text="75.2.60.5" tooltip="Copy value" />
            </TableRow>
          </Table>
        </section>
      </ContainerWithBackButton>
    );
  }

  if (!deploymentInfo) {
    return null;
  }

  return (
    <ContainerWithBackButton className="gap-6" title="DNS configuration" onClose={onCancel}>
      <p className="text-sm text-bolt-ds-textSecondary">
        Log in to the account you have with your DNS provider, and add a CNAME record for{' '}
        <span className="font-semibold text-bolt-ds-textPrimary">{entry.domain}</span> pointing to{' '}
        <span className="font-semibold text-bolt-ds-textPrimary">{BOLT_HOSTING_DNS}.</span>
      </p>

      <Table className="w-full text-sm">
        <TableHead className="grid grid-cols-[1fr_3fr_3fr] gap-2">
          <p>Type</p>
          <p>Name</p>
          <p>Value</p>
        </TableHead>

        <TableRow className="grid grid-cols-[1fr_3fr_3fr] bg-bolt-ds-surface text-bolt-ds-textPrimary gap-2">
          <p className="min-w-0">CNAME</p>
          <CellWithCopyButton text={entry.domain} tooltip="Copy name" />
          <CellWithCopyButton text={BOLT_HOSTING_DNS} tooltip="Copy value" />
        </TableRow>
      </Table>
    </ContainerWithBackButton>
  );
});

const CellWithCopyButton = ({ text, tooltip }: { text: string; tooltip: string }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setIsCopied(true);

    setTimeout(() => {
      setIsCopied(false);
    }, COPIED_MESSAGE_TIMEOUT);
  }, [text]);

  return (
    <p className="flex items-center gap-1 min-w-0">
      <span className="truncate min-w-0">{text}</span>
      <IconButton
        className="text-bolt-ds-textSecondary flex-shrink-0"
        title={tooltip}
        variant="ghost"
        sideOffset={2}
        size="xs"
        onClick={handleCopy}
        icon={isCopied ? 'i-ph:check' : 'i-ph:copy'}
      />
    </p>
  );
};
